import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, RefreshCw } from 'lucide-react';
import {
  fetchFeedbackCampaignAnalytics,
  feedbackCampaignCsvUrl,
  FeedbackCampaignAnalytics as Analytics,
} from '../services/api.ts';
import './FeedbackCampaignAnalytics.css';

const FeedbackCampaignAnalytics: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setData(await fetchFeedbackCampaignAnalytics(Number(id)));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    if (!id) return;
    const token = localStorage.getItem('auth_token');
    const res = await fetch(feedbackCampaignCsvUrl(Number(id)), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `campaign_${id}_responses.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="feedback-analytics-page"><div className="loading-spinner">Loading...</div></div>;
  }
  if (error || !data) {
    return (
      <div className="feedback-analytics-page">
        <div className="alert alert-error">{error || 'No data'}</div>
      </div>
    );
  }

  const { campaign, shown_count, submitted_count, dismissed_count, pending_count, completion_rate } = data;
  const target = campaign.target_sample_size;
  const fillPct = target > 0 ? Math.min(100, Math.round((shown_count / target) * 100)) : 0;
  const completionPct = Math.round((completion_rate || 0) * 100);

  const renderHistogram = (hist?: Record<string, number>) => {
    if (!hist) return null;
    const entries = Object.entries(hist).sort((a, b) => {
      const an = Number(a[0]); const bn = Number(b[0]);
      if (!Number.isNaN(an) && !Number.isNaN(bn)) return an - bn;
      return a[0].localeCompare(b[0]);
    });
    const max = Math.max(1, ...entries.map(([, v]) => v));
    return (
      <div className="histogram">
        {entries.map(([k, v]) => (
          <div key={k} className="histogram-row">
            <span className="histogram-key">{k}</span>
            <div className="histogram-bar-container">
              <div className="histogram-bar" style={{ width: `${(v / max) * 100}%` }} />
            </div>
            <span className="histogram-value">{v}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="feedback-analytics-page">
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/dashboard/feedback-campaigns')}>
          <ArrowLeft size={16} /> Back
        </button>
        <h1>{campaign.title}</h1>
        <div className="page-header-actions">
          <button className="btn-refresh" onClick={load}><RefreshCw size={16} /></button>
          <button className="btn-secondary" onClick={downloadCsv}>
            <Download size={16} /> Export CSV
          </button>
        </div>
      </div>

      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-value">{shown_count} / {target}</div>
          <div className="kpi-label">Shown to users</div>
          <div className="kpi-progress">
            <div className="kpi-progress-fill" style={{ width: `${fillPct}%` }} />
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-value">{submitted_count}</div>
          <div className="kpi-label">Submitted</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-value">{dismissed_count}</div>
          <div className="kpi-label">Dismissed</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-value">{pending_count}</div>
          <div className="kpi-label">Pending</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-value">{completionPct}%</div>
          <div className="kpi-label">Completion rate</div>
        </div>
      </div>

      <div className="questions-section">
        {data.per_question.map((q) => (
          <div className="question-card" key={q.question_id}>
            <div className="question-card-header">
              <h3>{q.text}</h3>
              <div className="question-card-meta">
                <span className="badge">{q.type}</span>
                <span className="answer-count">{q.answer_count} answers</span>
                {q.average !== undefined && q.average !== null && (
                  <span className="answer-count">avg {q.average.toFixed(2)}</span>
                )}
              </div>
            </div>
            {q.histogram && renderHistogram(q.histogram)}
            {q.sample_answers && (
              <ul className="text-answers">
                {q.sample_answers.length === 0 && <li className="muted">No text answers yet</li>}
                {q.sample_answers.map((t, i) => <li key={i}>{t}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FeedbackCampaignAnalytics;
